/**
 * Layout helpers for React Flow
 * Converts nested tree to React Flow nodes/edges using Dagre
 */

import dagre from 'dagre';

/**
 * Convert nested OrgNode tree to React Flow nodes and edges
 */
export function treeToFlowElements(tree, options = {}) {
  const { focusNodeId = null } = options;
  
  const nodes = [];
  const edges = [];
  
  if (!tree) return { nodes, edges };
  
  // Traverse tree and collect nodes/edges
  function traverse(node, parentId = null) {
    // Create node
    const flowNode = {
      id: node.id,
      type: 'custom',
      data: {
        name: node.name,
        plan: node.plan,
        agencyCode: node.agencyCode,
        email: node.email,
        profilePicture: node.profilePicture || node.photoURL || '',
        photoURL: node.photoURL || node.profilePicture || '',
        level: node.level || node.plan || '',
        isBrokerSubRoot: node.isBrokerSubRoot || false,
        isFocused: node.id === focusNodeId,
        // Include tracker flags for filter styling
        training: !!node.training,
        bigEvent: !!node.bigEvent,
        keyPlayer: !!node.keyPlayer,
        netLicenseAmount: node.netLicenseAmount || 0,
        // Include licensing and production flags
        licensed: !!node.licensed,
        hasProduction: !!node.hasProduction
      },
      position: { x: 0, y: 0 } // Will be set by layout
    };
    
    nodes.push(flowNode);
    
    // Create edge from parent
    if (parentId) {
      edges.push({
        id: `${parentId}-${node.id}`,
        source: parentId,
        target: node.id,
        type: 'smoothstep',
        animated: false,
        style: { stroke: '#94a3b8', strokeWidth: 2 }
      });
    }
    
    // Traverse children
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => traverse(child, node.id));
    }
  }
  
  traverse(tree);
  
  return { nodes, edges };
}

/**
 * Apply Dagre layout to nodes and edges
 */
export function applyDagreLayout(nodes, edges, options = {}) {
  const {
    direction = 'TB', // Top to Bottom
    nodeWidth = 180,
    nodeHeight = 220,
    rankSep = 120,
    nodeSep = 100
  } = options;
  
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  
  // Set graph properties
  dagreGraph.setGraph({
    rankdir: direction,
    ranksep: rankSep,
    nodesep: nodeSep
  });
  
  // Add nodes to dagre graph
  nodes.forEach(node => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });
  
  // Add edges to dagre graph
  edges.forEach(edge => {
    dagreGraph.setEdge(edge.source, edge.target);
  });
  
  // Calculate layout
  dagre.layout(dagreGraph);
  
  // Apply positions to nodes
  const layoutedNodes = nodes.map(node => {
    const nodeWithPosition = dagreGraph.node(node.id);
    
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2
      }
    };
  });
  
  return { nodes: layoutedNodes, edges };
}

/**
 * Convert tree to React Flow with layout applied
 */
export function layoutTree(tree, options = {}) {
  const { nodes: rawNodes, edges } = treeToFlowElements(tree, options);
  const { nodes: layoutedNodes } = applyDagreLayout(rawNodes, edges, options);
  
  return { nodes: layoutedNodes, edges };
}

/**
 * Create a grid layout for multiple trees (used in SuperBase view)
 */
export function createGridLayout(brokers, options = {}) {
  const { columns = 3, spacing = 20 } = options;
  
  const nodes = [];
  const edges = [];
  
  brokers.forEach((broker, index) => {
    const row = Math.floor(index / columns);
    const col = index % columns;
    
    const node = {
      id: broker.id || broker.name.toLowerCase().replace(/\s+/g, '-'),
      type: 'brokerCard',
      data: {
        name: broker.name,
        plan: broker.plan,
        email: broker.email,
        broker: broker
      },
      position: {
        x: col * (250 + spacing),
        y: row * (120 + spacing)
      }
    };
    
    nodes.push(node);
  });
  
  return { nodes, edges };
}

/**
 * Find node position by ID for centering view
 */
export function findNodePosition(nodes, nodeId) {
  const node = nodes.find(n => n.id === nodeId);
  return node ? node.position : null;
}

